/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { GameCanvas } from './components/GameCanvas';
import { GameHUD } from './components/GameHUD';
import { MainMenu } from './components/MainMenu';
import { PauseOverlay } from './components/PauseOverlay';
import { GameOverOverlay } from './components/GameOverlay';
import { getMuted, setMuted } from './utils/audio';
import { GameDifficulty, GameScreen } from './types';
import { Plane, ShieldAlert, Award, Star } from 'lucide-react';

export default function AirStrikeGame() {
  // Screens state
  const [screen, setScreen] = useState<GameScreen>('start');
  const [difficulty, setDifficulty] = useState<GameDifficulty>('rookie');

  // Track live session stats updated directly by the high-performance physics loops
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  const [kills, setKills] = useState(0);
  const [level, setLevel] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Dynamic state callbacks for high-speed overlay updates (HUD)
  const [playerStats, setPlayerStats] = useState({
    health: 100,
    maxHealth: 100,
    shield: 50,
    maxShield: 50,
    missiles: 5,
    maxMissiles: 10,
    xp: 0,
    xpNeeded: 100,
    combo: 1,
  });

  // Reset request hook trigger
  const [resetTicker, setResetTicker] = useState(false);

  // Hydrate high score from local storage
  useEffect(() => {
    const cachedBest = localStorage.getItem('airstrip_best');
    if (cachedBest) {
      setBestScore(parseInt(cachedBest, 10));
    }
  }, []);

  // Sync isMuted value to sound player
  const handleToggleMute = () => {
    const nextVal = !isMuted;
    setIsMuted(nextVal);
    setMuted(nextVal);
  };

  // Callback from Game Loop reporting progress
  const handlePlayerStatsUpdate = (
    health: number, maxHealth: number,
    shield: number, maxShield: number,
    missiles: number, maxMissiles: number,
    xp: number, xpNeeded: number,
    combo: number
  ) => {
    setPlayerStats({
      health,
      maxHealth,
      shield,
      maxShield,
      missiles,
      maxMissiles,
      xp,
      xpNeeded,
      combo,
    });
  };

  // Keyboard support to toggle Pause using Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (screen === 'playing') {
          setScreen('paused');
        } else if (screen === 'paused') {
          setScreen('playing');
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [screen]);

  // Restart active run actions
  const triggerRestart = () => {
    setResetTicker(true);
    setScreen('playing');
  };

  return (
    <div id="air-strike-app" className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-4 selection:bg-cyan-500 selection:text-black">
      
      {/* GLOWING HEADER OVERVIEW SECTION */}
      <div className="w-full max-w-5xl mb-4 flex flex-col sm:flex-row items-center justify-between gap-4 mt-2 px-1">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-cyan-500 to-sky-600 rounded-xl shadow-[0_0_15px_rgba(6,182,212,0.3)] animate-pulse border border-cyan-400/40">
            <Plane className="w-6 h-6 text-black rotate-45" />
          </div>
          <div>
            <h2 className="text-xl font-mono font-black tracking-tight text-white uppercase flex items-center gap-1.5">
              AIR STRIKE TRAINING <span className="text-[10px] bg-slate-900 border border-slate-800 text-cyan-400 px-1.5 py-0.5 rounded">2D ARCADE</span>
            </h2>
            <p className="text-xs text-slate-400 font-mono">Auto target-lock fighter defense platform</p>
          </div>
        </div>

        {/* Global mini record dashboard */}
        <div className="flex gap-4 font-mono text-xs text-slate-400">
          <div className="bg-slate-900/60 px-3.5 py-1.5 rounded-lg border border-slate-800/80 flex items-center gap-1.5">
            <Award className="w-3.5 h-3.5 text-yellow-400" />
            RECORD: <span className="text-yellow-400 font-bold">{bestScore.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* CORE DISPLAY WINDOW VIEWPORTS */}
      <div className="relative w-full max-w-5xl bg-slate-900/40 border border-slate-800/60 rounded-2xl p-2 sm:p-4 backdrop-blur-sm shadow-3xl space-y-4">
        
        {screen === 'start' ? (
          <MainMenu 
            difficulty={difficulty}
            setDifficulty={setDifficulty}
            onStartGame={() => setScreen('playing')}
            bestScore={bestScore}
            isMuted={isMuted}
            toggleMute={handleToggleMute}
          />
        ) : (
          <div className="relative flex flex-col gap-4">
            
            {/* The interactive physics canvas container */}
            <div className="relative overflow-hidden rounded-xl">
              <GameCanvas 
                difficulty={difficulty}
                screen={screen}
                setScreen={setScreen}
                score={score}
                setScore={setScore}
                bestScore={bestScore}
                setBestScore={setBestScore}
                kills={kills}
                setKills={setKills}
                level={level}
                setLevel={setLevel}
                resetRequested={resetTicker}
                onResetComplete={() => setResetTicker(false)}
                onPlayerStatsUpdate={handlePlayerStatsUpdate}
              />

              {/* OVERLAYS ABSOLUTE POSITIONED DIRECTLY ON CANVAS */}
              {screen === 'paused' && (
                <PauseOverlay 
                  onResume={() => setScreen('playing')}
                  onRestart={triggerRestart}
                  onExit={() => setScreen('start')}
                  score={score}
                  level={level}
                  kills={kills}
                />
              )}

              {screen === 'gameover' && (
                <GameOverOverlay 
                  score={score}
                  bestScore={bestScore}
                  levelReached={level}
                  kills={kills}
                  difficulty={difficulty}
                  onRestart={triggerRestart}
                  onExit={() => setScreen('start')}
                />
              )}
            </div>

            {/* LIVE COCKPIT HUD COMPONENT DISPLAY */}
            <GameHUD 
              health={playerStats.health}
              maxHealth={playerStats.maxHealth}
              shield={playerStats.shield}
              maxShield={playerStats.maxShield}
              missiles={playerStats.missiles}
              maxMissiles={playerStats.maxMissiles}
              xp={playerStats.xp}
              xpNeeded={playerStats.xpNeeded}
              combo={playerStats.combo}
              score={score}
              bestScore={bestScore}
              kills={kills}
              level={level}
              difficulty={difficulty}
              isMuted={isMuted}
              toggleMute={handleToggleMute}
              screen={screen === 'paused' ? 'paused' : 'playing'}
              setScreen={(s) => setScreen(s as GameScreen)}
            />

          </div>
        )}

      </div>

      {/* FOOTER NOTES SCREEN DEBRIS ACCENTS */}
      <div className="mt-4 text-[11px] font-mono text-slate-600 flex items-center gap-1">
        <Star className="w-3 h-3 text-cyan-400" /> Play with keyboard to maneuver, collect glowing capsules to reinforce jet weapons.
      </div>

    </div>
  );
}
